import { VastError } from './VastError';

/**
 * VAST 2.0 / 3.0 / 4.x parser.
 * - Resolves Wrapper chains (spec §3.7, merges tracking URLs at every level)
 * - Parses <ViewableImpression> (VAST 3+)
 * - Parses <Tracking event="progress" offset="…"> absolute + percentage
 * - Parses <AdVerifications> OMID block
 */
export class VastParser {
  constructor(opts = {}) {
    this.maxWrapperDepth = opts.maxWrapperDepth || 5;
    this.timeout = opts.timeout || 10000;
  }

  async fetchAndParse(url, depth = 0) {
    if (depth > this.maxWrapperDepth)
      throw new VastError(903, 'Wrapper depth exceeded');
    const xml = await this._fetch(url);
    const ad = this._extractAd(this._parseXML(xml));
    if (ad.type === 'wrapper')
      return this._mergeWrapper(ad, await this.fetchAndParse(ad.vastAdTagURI, depth + 1));
    return ad;
  }

  parseXML(str) {
    return this._extractAd(this._parseXML(str));
  }

  async _fetch(url) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), this.timeout);
    try {
      const r = await fetch(url, { signal: ctrl.signal });
      if (!r.ok) throw new VastError(301, 'HTTP ' + r.status);
      return r.text();
    } catch (e) {
      if (e.name === 'AbortError') throw new VastError(301, 'VAST fetch timeout');
      throw e;
    } finally {
      clearTimeout(t);
    }
  }

  _parseXML(str) {
    const doc = new DOMParser().parseFromString(str, 'text/xml');
    if (doc.querySelector('parsererror')) throw new VastError(100, 'XML parse error');
    return doc;
  }

  _extractAd(doc) {
    const adEl = doc.querySelector('Ad');
    if (!adEl) throw new VastError(303, 'No <Ad> element');
    const inline = adEl.querySelector(':scope > InLine');
    const wrapper = adEl.querySelector(':scope > Wrapper');

    if (wrapper) {
      const uri = this._text(wrapper, 'VASTAdTagURI');
      if (!uri) throw new VastError(101, 'Wrapper missing VASTAdTagURI');
      return {
        type: 'wrapper', id: adEl.getAttribute('id') || '',
        vastAdTagURI: uri,
        impressions: this._allText(wrapper, 'Impression'),
        errors: this._allText(wrapper, 'Error'),
        trackingEvents: this._parseTracking(wrapper),
        clickTracking: this._allText(wrapper, 'ClickTracking'),
      };
    }
    if (inline) {
      return {
        type: 'inline', id: adEl.getAttribute('id') || '',
        adTitle: this._text(inline, 'AdTitle'),
        advertiser: this._text(inline, 'Advertiser'),
        adSystem: this._text(inline, 'AdSystem'),
        impressions: this._allText(inline, 'Impression'),
        errors: this._allText(inline, 'Error'),
        linear: this._parseLinear(inline),
        viewableImpression: this._parseViewableImpression(inline),
        adVerifications: this._parseAdVerifications(inline),
      };
    }
    throw new VastError(100, 'Ad has neither InLine nor Wrapper');
  }

  _parseLinear(node) {
    const l = node.querySelector('Linear');
    if (!l) return null;
    const dur = this._parseDuration(this._text(l, 'Duration'));
    const sk = l.getAttribute('skipoffset');
    return {
      duration: dur,
      skipoffset: sk ? this._parseOffset(sk, dur) : null,
      mediaFiles: this._parseMediaFiles(l),
      trackingEvents: this._parseTracking(l),
      progressOffsets: this._parseProgressOffsets(l),
      clickThrough: this._text(l, 'ClickThrough'),
      clickTracking: this._allText(l, 'ClickTracking'),
    };
  }

  _parseMediaFiles(node) {
    return Array.from(node.querySelectorAll('MediaFile'))
      .map(m => ({
        url: m.textContent.trim(),
        type: (m.getAttribute('type') || '').toLowerCase(),
        width: +m.getAttribute('width') || 0,
        height: +m.getAttribute('height') || 0,
        bitrate: +m.getAttribute('bitrate') || 0,
      })).filter(m => m.url);
  }

  _parseTracking(node) {
    const map = {};
    node.querySelectorAll('TrackingEvents Tracking').forEach(t => {
      const ev = t.getAttribute('event');
      const url = t.textContent.trim();
      if (!ev || !url || ev === 'progress') return;
      (map[ev] = map[ev] || []).push(url);
    });
    return map;
  }

  _parseProgressOffsets(node) {
    const offsets = [];
    node.querySelectorAll('TrackingEvents Tracking[event="progress"]').forEach(t => {
      const raw = t.getAttribute('offset') || '';
      const url = t.textContent.trim();
      if (!url) return;
      const isPct = raw.includes('%');
      const seconds = isPct ? null : this._parseDuration(raw);
      const pct = isPct ? parseFloat(raw) : null;
      const existing = offsets.find(o => o.seconds === seconds && o.pct === pct);
      if (existing) existing.urls.push(url);
      else offsets.push({ seconds, pct, urls: [url] });
    });
    return offsets;
  }

  _parseViewableImpression(node) {
    const vi = node.querySelector('ViewableImpression');
    if (!vi) return null;
    return {
      id: vi.getAttribute('id') || '',
      viewable: this._allText(vi, 'Viewable'),
      notViewable: this._allText(vi, 'NotViewable'),
      viewUndetermined: this._allText(vi, 'ViewUndetermined'),
    };
  }

  _parseAdVerifications(node) {
    return Array.from(node.querySelectorAll('AdVerifications Verification')).map(v => ({
      vendor: v.getAttribute('vendor') || '',
      scriptUrl: v.querySelector('JavaScriptResource')?.textContent.trim() || '',
      verificationParameters: v.querySelector('VerificationParameters')?.textContent.trim() || '',
      notExecutedPixels: this._allText(v, 'TrackingEvents Tracking'),
    }));
  }

  _mergeWrapper(wrapper, child) {
    if (child.type !== 'inline') return child;
    child.impressions = [...(wrapper.impressions || []), ...(child.impressions || [])];
    child.errors = [...(wrapper.errors || []), ...(child.errors || [])];
    if (child.linear) {
      const m = { ...child.linear.trackingEvents };
      Object.keys(wrapper.trackingEvents || {}).forEach(ev =>
        m[ev] = [...(wrapper.trackingEvents[ev] || []), ...(m[ev] || [])]
      );
      child.linear.trackingEvents = m;
      child.linear.clickTracking = [
        ...(wrapper.clickTracking || []),
        ...(child.linear.clickTracking || []),
      ];
    }
    return child;
  }

  _text(n, s) { const e = n.querySelector(s); return e ? e.textContent.trim() : ''; }
  _allText(n, s) {
    return Array.from(n.querySelectorAll(s)).map(e => e.textContent.trim()).filter(Boolean);
  }
  _parseDuration(s) {
    if (!s) return 0;
    const p = s.split(':');
    if (p.length === 3) return +p[0] * 3600 + +p[1] * 60 + parseFloat(p[2]);
    return parseFloat(s) || 0;
  }
  _parseOffset(v, d) {
    return v.includes('%') ? d * parseFloat(v) / 100 : this._parseDuration(v);
  }
}